import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listItems } from '../items/api';
import { listLocations } from '../locations/api';
import { deletePickFace, listPickFaces, savePickFace, type PickFaceDto } from './api';

/**
 * Pick faces — where each item is picked from.
 *
 * This is what the directed pick walks. Without a face an item still appears on the route, but with
 * nowhere to send anybody, and the route sorts by nothing in particular — legacy's own checker says
 * as much. legacy gives this its own screen (ForwardPickController) and so does this.
 *
 * **An item may have more than one face in a warehouse**, and that is the point rather than a
 * mistake to guard against: a fast mover sitting at both ends of an aisle becomes two stops on the
 * route, each offering the whole outstanding quantity, because by the time the picker reaches the
 * second the figure has fallen.
 *
 * **Warehouse-scoped with no warehouse picker.** The faces shown are the current warehouse's, and
 * assigning one takes the warehouse from the session: a face in another warehouse is something the
 * server refuses, so offering it as a choice would only be offering a mistake.
 *
 * The walk order itself lives on the LOCATION and is edited on the Locations screen — one number
 * per location rather than per item, which is what makes a route a route.
 */
export function PickFacesRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const warehouseId = useCurrentWarehouseId();

  const [search, setSearch] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);

  const canWrite = permissions.has('locations:write');

  const faces = useQuery({
    queryKey: ['pick-faces', warehouseId] as const,
    queryFn: ({ signal }) => listPickFaces({}, signal),
  });

  const items = useQuery({
    queryKey: ['items', 'for-pick-faces'] as const,
    queryFn: ({ signal }) => listItems(false, signal),
    enabled: canWrite,
  });

  const locations = useQuery({
    queryKey: ['locations', warehouseId, 'for-pick-faces'] as const,
    queryFn: ({ signal }) => listLocations({ warehouseId }, signal),
    enabled: canWrite && warehouseId !== '',
  });

  const save = useMutation({
    mutationFn: () => savePickFace(itemId!, locationId!),
    onSuccess: (face) => {
      notifications.show({
        color: 'green',
        message: t('pickFaces.saved', { item: face.itemNumber, location: face.locationCode }),
      });
      setLocationId(null);
      void queryClient.invalidateQueries({ queryKey: ['pick-faces'] });
    },
    onError: (error: Error) => {
      notifications.show({
        color: 'red',
        title: t('pickFaces.errors.saveFailed'),
        message: error instanceof ApiError ? error.problem.detail ?? error.message : error.message,
      });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePickFace(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pick-faces'] });
    },
    onError: (error: Error) => {
      notifications.show({
        color: 'red',
        title: t('pickFaces.errors.removeFailed'),
        message: error.message,
      });
    },
  });

  const rows = useMemo(() => {
    const all = faces.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (f) =>
        f.itemNumber.toLowerCase().includes(needle) ||
        f.itemDescription.toLowerCase().includes(needle) ||
        f.locationCode.toLowerCase().includes(needle),
    );
  }, [faces.data, search]);

  const columns: DataColumn<PickFaceDto>[] = useMemo(
    () => [
      {
        key: 'item',
        label: t('pickFaces.item'),
        card: 'title',
        render: (f) => <Code>{f.itemNumber}</Code>,
      },
      {
        key: 'description',
        label: t('pickFaces.description'),
        card: 'subtitle',
        render: (f) => f.itemDescription,
      },
      {
        key: 'location',
        label: t('pickFaces.location'),
        render: (f) => <Code>{f.locationCode}</Code>,
      },
      {
        key: 'sequence',
        label: t('pickFaces.sequence'),
        card: 'aside',
        // A face with no sequence is not broken, it is just walked last. Saying so here is the
        // only place anybody would notice before a picker does.
        render: (f) =>
          f.sequence === null ? (
            <Text size="sm" c="orange">
              {t('pickFaces.unsequenced')}
            </Text>
          ) : (
            <Text size="sm">{f.sequence}</Text>
          ),
      },
      {
        key: 'flags',
        label: t('pickFaces.locationStatus'),
        render: (f) => (
          <Stack gap={2}>
            {!f.isForwardPick && (
              <Text size="xs" c="dimmed">
                {t('pickFaces.notFlagged')}
              </Text>
            )}
            {!f.locationIsActive && (
              <Text size="xs" c="red">
                {t('pickFaces.locationInactive')}
              </Text>
            )}
            {f.isForwardPick && f.locationIsActive && (
              <Text size="xs" c="dimmed">
                {t('pickFaces.ok')}
              </Text>
            )}
          </Stack>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader title={t('nav.pickFaces')} />

      <Stack gap="md">
        {canWrite && (
          <Card withBorder padding="md">
            <Stack gap="sm">
              <Text fw={500}>{t('pickFaces.assign')}</Text>
              <Group gap="sm" wrap="wrap" align="flex-end">
                <Select
                  label={t('pickFaces.item')}
                  placeholder={t('pickFaces.chooseItem')}
                  searchable
                  clearable
                  value={itemId}
                  onChange={setItemId}
                  data={(items.data ?? []).map((i) => ({
                    value: i.itemId,
                    label: `${i.itemNumber} — ${i.description}`,
                  }))}
                  w={{ base: '100%', sm: 320 }}
                />
                <Select
                  label={t('pickFaces.location')}
                  placeholder={t('pickFaces.chooseLocation')}
                  searchable
                  clearable
                  value={locationId}
                  onChange={setLocationId}
                  data={(locations.data ?? []).map((l) => ({
                    value: l.locationId,
                    // The walk order is shown while choosing, because that is the decision being
                    // made: not "which shelf" but "where in the route".
                    label: l.sequence === null
                      ? `${l.locationCode} — ${t('pickFaces.unsequenced')}`
                      : `${l.locationCode} — ${t('pickFaces.sequenceShort', { n: l.sequence })}`,
                  }))}
                  w={{ base: '100%', sm: 320 }}
                />
                <Button
                  color="invetrak"
                  leftSection={<IconPlus size={16} />}
                  disabled={!itemId || !locationId || save.isPending}
                  loading={save.isPending}
                  onClick={() => save.mutate()}
                >
                  {t('pickFaces.add')}
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                {t('pickFaces.severalFacesHint')}
              </Text>
            </Stack>
          </Card>
        )}

        <TextInput
          label={t('pickFaces.search')}
          placeholder={t('pickFaces.searchPlaceholder')}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={{ base: '100%', sm: 320 }}
        />

        {faces.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {faces.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('pickFaces.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(faces.error as Error).message}</Text>
              <Group>
                <Button size="xs" variant="default" onClick={() => void faces.refetch()}>
                  {t('picks.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {faces.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {search.trim() ? t('pickFaces.noMatch') : t('pickFaces.empty')}
          </Alert>
        )}

        {faces.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(f) => f.itemPickLocationId}
            minWidth={800}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={(f) =>
              canWrite ? (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={t('pickFaces.remove', {
                    item: f.itemNumber,
                    location: f.locationCode,
                  })}
                  onClick={() => remove.mutate(f.itemPickLocationId)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              ) : null
            }
          />
        )}
      </Stack>
    </>
  );
}
